import { Dtos, formatToDollar } from "@inrev/common";
import { SummaryCardSection, SummaryField, SummaryFieldGroup } from "@portal/components/multiform";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { match } from "ts-pattern";
import { boolToYN } from "../../../../../../utils";
import { NAICSCodesList } from "../../../../../admin/shared/NAICSCodeList";
import { type AccountFormValues, fp } from "../../schema";
import { useShowContractHistoryQuestions } from "./useShowContractHistoryQuestions";

type AccountRequest = Dtos.SuretyAccount.Create.Request["account"];

type SmallAccount = Extract<AccountRequest, { modelSize: "small" }>;
type MediumAccount = Extract<AccountRequest, { modelSize: "medium" }>;

const getBaseFields = (
	naicsCodes: SmallAccount["naicsCodes"],
	history: SmallAccount["history"],
	to: (id?: string) => { pathname: string; section: string; id?: string },
	showContractHistoryQuestions: boolean,
) => [
	<SummaryField
		key="naicsCodes"
		label="What type of work do the companies in this account specialize in?"
		value={
			<NAICSCodesList
				naicsCodes={naicsCodes}
				emptyMessage="--"
				className="my-[8px] bg-surface-neutral"
				hideExpand
				showCount
			/>
		}
		fieldPath={fp("account.naicsCodes")}
		to={to("naicsCodes")}
		vertical
	/>,
	...(showContractHistoryQuestions
		? [
				<SummaryField
					key="largestProjectCompleted"
					label="What is the value of the largest project completed by the companies in this account?"
					value={formatToDollar(history.largestProjectCompleted)}
					fieldPath={fp("account.history.largestProjectCompleted")}
					to={to("largestProjectCompleted")}
					vertical
				/>,
				<SummaryField
					key="failedToCompleteAProject"
					label="Has any company in this account ever failed to complete a project?"
					value={boolToYN(history.failedToCompleteAProject)}
					fieldPath={fp("account.history.failedToCompleteAProject")}
					to={to("failedToCompleteAProject")}
					vertical
				/>,
				<SummaryField
					key="lostAPaymentSuit"
					label="Has any company in this account ever lost a payment suit?"
					value={boolToYN(history.lostAPaymentSuit)}
					fieldPath={fp("account.history.lostAPaymentSuit")}
					to={to("lostAPaymentSuit")}
					vertical
				/>,
			]
		: []),
	<SummaryField
		key="causedSuretyLoss"
		label="Has any company in this account ever caused a loss to a surety?"
		value={boolToYN(history.causedSuretyLoss)}
		fieldPath={fp("account.history.causedSuretyLoss")}
		to={to("causedSuretyLoss")}
		vertical
	/>,
];

const getMediumFields = (
	history: MediumAccount["history"],
	to: (id?: string) => { pathname: string; section: string; id?: string },
) => [
	<SummaryField
		key="constructionProjectsGrossLossGt10PercentLast36Months"
		label="In the past 3 years, has any company in this account experienced a gross loss on any project greater than 10% of that project's contract value?"
		value={boolToYN(history.constructionProjectsGrossLossGt10PercentLast36Months)}
		fieldPath={fp("account.history.constructionProjectsGrossLossGt10PercentLast36Months")}
		to={to("constructionProjectsGrossLossGt10PercentLast36Months")}
		vertical
	/>,
	<SummaryField
		key="currentConstructionProjectExpectedGrossLoss"
		label="Does any company in this account expect a gross loss on a project they are currently working on?"
		value={boolToYN(history.currentConstructionProjectExpectedGrossLoss)}
		fieldPath={fp("account.history.currentConstructionProjectExpectedGrossLoss")}
		to={to("currentConstructionProjectGrossLoss")}
		vertical
	/>,
];

export const HistorySummary = () => {
	const { getValues } = useFormContext<AccountFormValues>();
	const { pathname } = useLocation();
	const values = getValues();
	const account = values.account;
	const to = (id?: string) => ({ pathname, section: "history", id });
	const showContractHistoryQuestions = useShowContractHistoryQuestions();

	const fields = match(account)
		.with({ modelSize: "medium" }, { modelSize: "large" }, (a) => [
			...getBaseFields(a.naicsCodes, a.history, to, showContractHistoryQuestions),
			...(showContractHistoryQuestions ? getMediumFields(a.history, to) : []),
		])
		.otherwise(() =>
			getBaseFields(account.naicsCodes, account.history, to, showContractHistoryQuestions),
		);

	return (
		<SummaryCardSection header="History" to={{ pathname, section: "history" }}>
			<SummaryFieldGroup>{fields}</SummaryFieldGroup>
		</SummaryCardSection>
	);
};
